// First-run / zero-projects empty state for the CRM shell. Replaces the entire
// shell when the user has no CRM projects.
//
// org_admin first visit (no companies yet):
//   Step 1 — optional company setup (skip allowed)
//   Step 2 — create first project
//   Step 3 — optional add members to the new project
//
// Other admins → project step, then optional members. Non-admins → contact-admin message.

import React, { useEffect, useMemo, useState } from "react";
import { FolderPlus, Sparkles, Loader2, ArrowRight, UserCheck, Mail, Building2, Users, Search, Check } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "../atoms";
import { useCreateProject } from "../hooks/useProjects";
import { useMyCrmRole, useTeamMembers, useEligibleUsers, useAddCrmMember, formatMember } from "../hooks/useTeamMembers";
import { useCreateCompany, useCompanies } from "../hooks/useCompanies";
import { useProjectMembers, useSetProjectMembers } from "../hooks/useProjectMembers";
import { useAuth } from "../../../contexts/AuthContext";
import { getOrgId } from "../../../utils/organization";
import { useCrmFirstRun } from "../contexts/CrmFirstRunContext";

const ONBOARDING_KEY = (orgId) => `crm.companyOnboardingDone:${orgId}`;

function WelcomeShell({ children, icon: Icon, iconLoading }) {
  return (
    <div style={{
      flex: 1, minHeight: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 32,
      background: "var(--color-bg-base)",
      position: "relative",
      overflow: "hidden",
    }}>
      <div aria-hidden style={{
        position: "absolute", top: "-20%", right: "-10%",
        width: 480, height: 480, borderRadius: "50%",
        background: "radial-gradient(circle, var(--crm-primary-soft) 0%, transparent 70%)",
        opacity: 0.6, pointerEvents: "none",
      }} />
      <div aria-hidden style={{
        position: "absolute", bottom: "-20%", left: "-10%",
        width: 420, height: 420, borderRadius: "50%",
        background: "radial-gradient(circle, var(--crm-primary-soft) 0%, transparent 70%)",
        opacity: 0.4, pointerEvents: "none",
      }} />
      <div style={{
        position: "relative",
        width: "100%", maxWidth: 460,
        background: "var(--color-bg-surface)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: 20,
        padding: "40px 36px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.08), 0 1px 0 rgba(255,255,255,0.04) inset",
        textAlign: "center",
      }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 12px", borderRadius: 999,
          background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
          fontSize: 11, fontWeight: 600, marginBottom: 24,
        }}>
          <Sparkles size={11} /> Welcome to CRM
        </div>
        <div style={{
          width: 72, height: 72, borderRadius: 20,
          margin: "0 auto 20px",
          background: "linear-gradient(135deg, var(--crm-primary) 0%, var(--crm-primary-soft) 100%)",
          color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 8px 24px rgba(99, 102, 241, 0.25)",
        }}>
          {iconLoading ? <Loader2 size={28} className="animate-spin" /> : <Icon size={32} strokeWidth={2} />}
        </div>
        {children}
      </div>
    </div>
  );
}

function CompanyOnboardingStep({ orgId, onDone }) {
  const [name, setName] = useState("");
  const [showForm, setShowForm] = useState(false);
  const createMut = useCreateCompany();

  const markDone = () => {
    try { if (orgId) localStorage.setItem(ONBOARDING_KEY(orgId), "1"); } catch { /* ignore */ }
    onDone();
  };

  const skip = () => markDone();

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await createMut.mutateAsync({ name: trimmed });
      toast.success(`Company "${trimmed}" created`);
      markDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to create company");
    }
  };

  return (
    <WelcomeShell icon={Building2} iconLoading={false}>
      <p style={{ margin: "0 0 16px", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)" }}>
        Step 1 of 3 · Optional
      </p>
      <h1 style={{
        margin: "0 0 10px", fontSize: 24, fontWeight: 600,
        letterSpacing: "-0.02em", color: "var(--color-text-primary)", lineHeight: 1.2,
      }}>
        Add a company
      </h1>
      <p style={{
        margin: "0 auto 28px", maxWidth: 360,
        fontSize: 14, lineHeight: 1.55, color: "var(--color-text-secondary)",
      }}>
        If your org has multiple brands or business units, create a company to keep CRM data separate. You can skip and add companies later in Settings.
      </p>

      {!showForm ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          <Button variant="primary" size="lg" style={welcomeCtaStyle} onClick={() => setShowForm(true)} leftIcon={<Building2 size={15} />} rightIcon={<ArrowRight size={14} />}>
            Create company
          </Button>
          <button
            type="button"
            onClick={skip}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              fontSize: 13, fontWeight: 500, color: "var(--color-text-secondary)",
              textDecoration: "underline", padding: "4px 8px",
            }}
          >
            Skip — continue without companies
          </button>
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12, textAlign: "left" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={fieldLabel}>Company name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Cord4 Technologies"
              style={inputStyle}
              maxLength={100}
            />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <Button
              type="submit"
              disabled={!name.trim() || createMut.isPending}
              leftIcon={createMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Building2 size={13} />}
            >
              {createMut.isPending ? "Creating…" : "Create company"}
            </Button>
            <Button type="button" variant="secondary" onClick={() => { setShowForm(false); setName(""); }} disabled={createMut.isPending}>
              Back
            </Button>
          </div>
        </form>
      )}
    </WelcomeShell>
  );
}

function ProjectOnboardingStep({ stepLabel, onProjectCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [showForm, setShowForm] = useState(false);
  const createMut = useCreateProject();

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const project = await createMut.mutateAsync({ name: trimmed, description: description.trim() });
      toast.success(`Project "${trimmed}" created`);
      onProjectCreated?.(project);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to create project");
    }
  };

  return (
    <WelcomeShell icon={FolderPlus} iconLoading={false}>
      {stepLabel && (
        <p style={{ margin: "0 0 16px", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)" }}>
          {stepLabel}
        </p>
      )}
      <h1 style={{
        margin: "0 0 10px", fontSize: 24, fontWeight: 600,
        letterSpacing: "-0.02em", color: "var(--color-text-primary)", lineHeight: 1.2,
      }}>
        Create your first project
      </h1>
      <p style={{
        margin: "0 auto 28px", maxWidth: 340,
        fontSize: 14, lineHeight: 1.55, color: "var(--color-text-secondary)",
      }}>
        Projects are workspaces — every lead, pipeline, call and activity lives inside one.
      </p>

      {!showForm ? (
        <Button variant="primary" size="lg" style={welcomeCtaStyle} onClick={() => setShowForm(true)} leftIcon={<FolderPlus size={15} />} rightIcon={<ArrowRight size={14} />}>
          Create project
        </Button>
      ) : (
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12, textAlign: "left" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={fieldLabel}>Project name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme Outbound, Q3 Pipeline"
              style={inputStyle}
              maxLength={120}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={fieldLabel}>
              Description <span style={{ fontWeight: 500, color: "var(--color-text-secondary)", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this project for?"
              rows={2}
              style={{
                ...inputStyle,
                height: "auto", padding: "10px 12px",
                resize: "vertical", minHeight: 68, lineHeight: 1.5,
              }}
              maxLength={500}
            />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Button
              type="submit"
              disabled={!name.trim() || createMut.isPending}
              leftIcon={createMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <FolderPlus size={13} />}
            >
              {createMut.isPending ? "Creating…" : "Create project"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => { setShowForm(false); setName(""); setDescription(""); }}
              disabled={createMut.isPending}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </WelcomeShell>
  );
}

function formatEligibleUser(u) {
  if (!u) return "—";
  return u.name || [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email || "Unknown";
}

function MembersOnboardingStep({ projectId, projectName, stepLabel, onDone }) {
  const { user } = useAuth();
  const selfId = user?._id ? String(user._id) : null;
  const { data: assigned = [], isLoading: assignedLoading } = useProjectMembers(projectId);
  const { members: crmMembers, isLoading: crmLoading } = useTeamMembers();
  const { data: eligible = [], isLoading: eligibleLoading } = useEligibleUsers();
  const addCrmMut = useAddCrmMember();
  const setMembersMut = useSetProjectMembers(projectId);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const assignedIds = useMemo(
    () => new Set(assigned.map((a) => String(a.userId))),
    [assigned],
  );

  const crmMemberIds = useMemo(
    () => new Set(crmMembers.map((m) => String(m._id))),
    [crmMembers],
  );

  const candidates = useMemo(() => {
    const seen = new Set();
    const list = [];

    crmMembers.forEach((m) => {
      const id = String(m._id);
      if (id === selfId || assignedIds.has(id) || seen.has(id)) return;
      seen.add(id);
      list.push({ _id: id, label: formatMember(m), email: m.email || null, inCrm: true });
    });

    eligible.forEach((u) => {
      const id = String(u._id);
      if (id === selfId || assignedIds.has(id) || seen.has(id)) return;
      seen.add(id);
      list.push({ _id: id, label: formatEligibleUser(u), email: u.email || null, inCrm: false });
    });

    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(
      (u) => u.label.toLowerCase().includes(needle) || (u.email || "").toLowerCase().includes(needle),
    );
  }, [crmMembers, eligible, assignedIds, selfId, q]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const finish = async (userIds) => {
    setSaving(true);
    try {
      for (const userId of userIds) {
        if (!crmMemberIds.has(userId)) {
          await addCrmMut.mutateAsync({ userId, role: "agent" });
        }
      }
      if (userIds.length) {
        await setMembersMut.mutateAsync(userIds);
        toast.success(userIds.length === 1 ? "Member added to project" : `${userIds.length} members added to project`);
      }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to add members");
    } finally {
      setSaving(false);
    }
  };

  const isLoading = assignedLoading || crmLoading || eligibleLoading;

  return (
    <WelcomeShell icon={Users} iconLoading={false}>
      {stepLabel && (
        <p style={{ margin: "0 0 16px", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)" }}>
          {stepLabel}
        </p>
      )}
      <h1 style={{
        margin: "0 0 10px", fontSize: 24, fontWeight: 600,
        letterSpacing: "-0.02em", color: "var(--color-text-primary)", lineHeight: 1.2,
      }}>
        Add members to {projectName}
      </h1>
      <p style={{
        margin: "0 auto 24px", maxWidth: 380,
        fontSize: 14, lineHeight: 1.55, color: "var(--color-text-secondary)",
      }}>
        Invite teammates who have CRM access in TeamGrid. They&apos;ll be added to CRM and assigned to this project. You can skip and manage members later in Settings.
      </p>

      <div style={{ textAlign: "left", marginBottom: 16 }}>
        <div style={{ position: "relative", marginBottom: 10 }}>
          <Search size={13} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--color-text-secondary)" }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search team members"
            style={{ ...inputStyle, paddingLeft: 34 }}
            disabled={saving}
          />
        </div>
        <div style={{
          maxHeight: 220, overflowY: "auto",
          border: "1px solid var(--color-border-subtle)", borderRadius: 8,
          background: "var(--color-bg-base)",
        }}>
          {isLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 14, color: "var(--color-text-secondary)", fontSize: 13 }}>
              <Loader2 size={14} className="animate-spin" /> Loading team…
            </div>
          )}
          {!isLoading && candidates.length === 0 && (
            <div style={{ padding: 14, fontSize: 13, color: "var(--color-text-secondary)" }}>
              {q ? "No matches." : "No teammates available — enable CRM access in Team Management, or skip for now."}
            </div>
          )}
          {!isLoading && candidates.map((u) => {
            const checked = selected.has(u._id);
            return (
              <button
                key={u._id}
                type="button"
                onClick={() => toggle(u._id)}
                disabled={saving}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  width: "100%", padding: "10px 12px", border: "none",
                  background: checked ? "var(--crm-primary-soft)" : "transparent",
                  cursor: saving ? "default" : "pointer", textAlign: "left",
                  borderBottom: "1px solid var(--color-border-subtle)",
                }}
              >
                <span style={{
                  width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                  border: checked ? "none" : "1.5px solid var(--color-border-subtle)",
                  background: checked ? "var(--crm-primary)" : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff",
                }}>
                  {checked && <Check size={12} strokeWidth={3} />}
                </span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>{u.label}</div>
                  {u.email && <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{u.email}</div>}
                </span>
                {!u.inCrm && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: "var(--crm-primary)", }}>
                    New to CRM
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
        <Button
          variant="primary" size="lg" style={welcomeCtaStyle}
          onClick={() => finish([...selected])}
          disabled={saving || selected.size === 0}
          leftIcon={saving ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />}
          rightIcon={!saving && selected.size > 0 ? <ArrowRight size={14} /> : undefined}
        >
          {saving ? "Adding…" : selected.size ? `Add ${selected.size} member${selected.size === 1 ? "" : "s"} & continue` : "Select members to continue"}
        </Button>
        <button
          type="button"
          onClick={() => onDone()}
          disabled={saving}
          style={{
            background: "transparent", border: "none", cursor: saving ? "default" : "pointer",
            fontSize: 13, fontWeight: 500, color: "var(--color-text-secondary)",
            textDecoration: "underline", padding: "4px 8px",
          }}
        >
          Skip — open CRM without adding members
        </button>
      </div>
    </WelcomeShell>
  );
}

export default function NoProjectsWelcome() {
  const { user } = useAuth();
  const orgId = getOrgId(user);
  const isOrgAdmin = user?.role === "org_admin" || user?.role === "main_admin";
  const { isAdmin, isLoading: roleLoading } = useMyCrmRole();
  const { data: companies = [], isLoading: companiesLoading } = useCompanies({ enabled: isOrgAdmin });
  const { membersOnboarding, startMembersOnboarding, finishMembersOnboarding } = useCrmFirstRun();
  const [step, setStep] = useState(null);
  const [fromCompanyStep, setFromCompanyStep] = useState(false);

  const totalSteps = fromCompanyStep ? 3 : 2;
  const projectStepLabel = fromCompanyStep ? `Step 2 of ${totalSteps}` : `Step 1 of ${totalSteps}`;
  const membersStepLabel = `Step ${totalSteps} of ${totalSteps} · Optional`;

  const onboardingDone = useMemo(() => {
    try { return orgId ? localStorage.getItem(ONBOARDING_KEY(orgId)) === "1" : true; } catch { return true; }
  }, [orgId]);

  const activeCompanies = useMemo(
    () => companies.filter((c) => c.status !== "archived"),
    [companies]
  );

  const needsCompanyStep = isOrgAdmin && !onboardingDone && activeCompanies.length === 0;

  useEffect(() => {
    if (roleLoading || (isOrgAdmin && companiesLoading)) return;
    if (!isAdmin) {
      setStep(null);
      return;
    }
    setStep(needsCompanyStep ? "company" : "project");
  }, [roleLoading, companiesLoading, isAdmin, isOrgAdmin, needsCompanyStep]);

  if (roleLoading || (isOrgAdmin && companiesLoading) || (isAdmin && step === null)) {
    return (
      <WelcomeShell icon={Sparkles} iconLoading>
        <p style={{ margin: 0, color: "var(--color-text-secondary)", fontSize: 14 }}>Loading…</p>
      </WelcomeShell>
    );
  }

  if (!isAdmin) {
    return (
      <WelcomeShell icon={UserCheck} iconLoading={false}>
        <h1 style={{
          margin: "0 0 10px", fontSize: 24, fontWeight: 600,
          letterSpacing: "-0.02em", color: "var(--color-text-primary)", lineHeight: 1.2,
        }}>
          No projects assigned yet
        </h1>
        <p style={{
          margin: "0 auto 24px", maxWidth: 360,
          fontSize: 14, lineHeight: 1.55, color: "var(--color-text-secondary)",
        }}>
          You don&apos;t have access to any CRM projects yet. Reach out to your admin to get assigned to one — projects hold all the leads, pipelines and activities you&apos;ll work in.
        </p>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "10px 16px", borderRadius: 8,
          background: "var(--color-bg-muted)",
          color: "var(--color-text-primary)",
          fontSize: 13, fontWeight: 500,
        }}>
          <Mail size={14} style={{ color: "var(--crm-primary)" }} />
          Contact your admin to get started
        </div>
      </WelcomeShell>
    );
  }

  if (step === "company") {
    return (
      <CompanyOnboardingStep
        orgId={orgId}
        onDone={() => {
          setFromCompanyStep(true);
          setStep("project");
        }}
      />
    );
  }

  if (membersOnboarding) {
    return (
      <MembersOnboardingStep
        projectId={membersOnboarding.projectId}
        projectName={membersOnboarding.projectName}
        stepLabel={membersStepLabel}
        onDone={finishMembersOnboarding}
      />
    );
  }

  return (
    <ProjectOnboardingStep
      stepLabel={projectStepLabel}
      onProjectCreated={startMembersOnboarding}
    />
  );
}

// Sized up from Button's default lg (36px) — this is the one CTA on the
// screen, so it earns the extra emphasis every other CRM button doesn't need.
const welcomeCtaStyle = { height: 44, padding: "0 22px", fontSize: 14 };

const fieldLabel = {
  fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)",
};

const inputStyle = {
  width: "100%", height: 40, padding: "0 12px",
  borderRadius: 8, border: "1px solid var(--color-border-subtle)",
  background: "var(--color-bg-base)", color: "var(--color-text-primary)",
  fontSize: 14, outline: "none", fontFamily: "inherit",
  boxSizing: "border-box",
  transition: "border-color 120ms, box-shadow 120ms",
};
